import { ChangeDetectionStrategy, Component, type OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ButtonModule } from '@fino-ui/button';
import { ModalService } from '@fino-ui/modal';
import { TranslationModule } from '@fino-ui/translation';
import { filter } from 'lodash-es';
import { forkJoin, of } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { EnvironmentService } from 'src/app/core/environment/environment.service';
import { Routes } from 'src/app/core/routes/routes';
import { ActionBoxComponent } from '../../components/action-box/action-box.component';
import { HeaderComponent } from '../../components/header/header.component';
import { VideoModalComponent } from '../../components/video-modal/video-modal.component';
import { ARCHIVING_FLOW } from '../kwh/kwh.model';
import { KwhService } from '../kwh/kwh.service';
import { CockpitService } from '../kws/cockpit.service';
import { KwsService } from '../kws/kws.service';
import type { OverviewConfig } from '../overview/overview.model';
import { ActionLinksComponent } from './action-links/action-links.component';
import { KwhLinks, KwsLinks, OverviewLinks } from './home.constants';
import { HomeService } from './home.service';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.sass'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    HeaderComponent,
    ActionBoxComponent,
    ActionLinksComponent,
    ButtonModule,
    TranslationModule,
  ],
})
export class HomeComponent implements OnInit {
  constructor(
    private homeService: HomeService,
    private kwsService: KwsService,
    private kwhService: KwhService,
    private appService: AppService,
    private router: Router,
    private modalService: ModalService,
    private envService: EnvironmentService,
    private cockpitService: CockpitService,
  ) {}

  tenantSettings: OverviewConfig;
  Routes = Routes;

  kwhLinks = [];
  kwsLinks = [];
  cockpitLinks = [];
  overviewLinks = OverviewLinks;
  isArchivingActive: boolean;

  isKwhActive: boolean;
  isKwsActive: boolean;
  isDwsActive: boolean;

  canActivateKwh: boolean;
  canActivateKws: boolean;

  isCockpitEnabled: boolean;
  hasBestandskundenCockpit: boolean;

  get kwhTitle() {
    return `home.actions.kwh.title.${this.isKwhActive ? 'active' : 'deactive'}`;
  }

  get isNotProduction() {
    return !this.envService.isProd;
  }

  get kwsTitle() {
    return `home.actions.kws.title.${this.isKwsActive ? 'active' : 'deactive'}`;
  }

  get dwsTitle() {
    return `home.actions.dws.title.${this.isDwsActive ? 'active' : 'deactive'}`;
  }

  get dwsDescription() {
    return `home.actions.dws.subtitle.${this.isDwsActive ? 'active' : 'deactive'}`;
  }

  get kwhDescription() {
    return `home.actions.kwh.subtitle.${this.canActivateKwh ? 'active' : 'deactive'}`;
  }

  get kwsDescription() {
    return `home.actions.kws.subtitle.${this.canActivateKws ? 'active' : 'deactive'}`;
  }

  get isAtruvia() {
    return this.appService.isAtruvia;
  }

  ngOnInit(): void {
    const { availableProducts } = this.appService;

    const homeRequest = this.homeService.fetchOverview();

    const kwsRequest = availableProducts().hasKws
      ? this.kwsService.fetchKWS()
      : of({
          features: {
            kwsDisabled: true,
            kontowechselCockpit: { enabled: false, bestandskundenTenant: null },
          },
          canActivate: availableProducts().canConfigureKws,
        });
    const kwhRequest = availableProducts().hasKwh
      ? this.kwhService.fetchKWH()
      : of({
          isAidServiceActive: false,
          canActivate: availableProducts().canConfigureKwh,
          archiving: { flow: ARCHIVING_FLOW.NONE },
        });

    forkJoin({ homeRequest, kwsRequest, kwhRequest }).subscribe((resp) => {
      const { kwhRequest, kwsRequest, homeRequest } = resp;
      this.tenantSettings = homeRequest;
      this.appService.logoUrl.set(this.tenantSettings.logoUrl);

      this.isArchivingActive =
        kwhRequest.archiving.flow !== ARCHIVING_FLOW.NONE;

      this.isKwsActive = !kwsRequest.features.kwsDisabled;
      this.isKwhActive = kwhRequest.isAidServiceActive;
      this.isDwsActive = availableProducts().hasDws;
      this.isCockpitEnabled =
        !!kwsRequest.features.kontowechselCockpit?.enabled;
      this.hasBestandskundenCockpit =
        !!kwsRequest.features.kontowechselCockpit?.bestandskundenTenant;

      this.canActivateKws = this.isKwsActive || kwsRequest.canActivate;
      this.canActivateKwh = this.isKwhActive || kwhRequest.canActivate;

      this.setupLinks();
    });
  }

  open(route: string, section: string = null) {
    this.router.navigate([route], { queryParams: { section } });
  }

  openManual() {
    this.modalService.addModal(VideoModalComponent, {});
  }

  openBestandskundenCockpit() {
    this.cockpitService.openBestandskundenCockpit();
  }

  setupLinks() {
    this.kwsLinks = [
      ...KwsLinks,
      {
        name: 'kws.actions.dashboard.title',
        callback: () => this.kwsService.openDashboard(),
      },
    ];

    if (this.isCockpitEnabled) {
      this.kwsLinks = [
        ...this.kwsLinks,
        {
          name: 'kws.actions.cockpit.title',
          callback: () => this.cockpitService.openKwsCockpit(),
        },
      ];
    }

    this.kwhLinks = filter(KwhLinks, (link) => {
      if (link.name.includes('archiving') && !this.isArchivingActive)
        return false;
      return true;
    });

    if (this.isAtruvia) {
      this.kwhLinks = [
        ...this.kwhLinks,
        { name: 'kwh.actions.apiActivation.title' },
      ];
    }

    const blzFusionLink = {
      name: 'overview.actions.fusion.title',
      param: { modal: 'openFusionModal' },
    };

    if (this.isKwhActive) {
      this.overviewLinks = [...OverviewLinks, blzFusionLink];
    }
  }
}
